import type {
    AppDescriptionContext,
    ComponentBuilder,
    ComponentDescriptor,
    EnumPropertyCase,
    PropertyDescriptor,
} from ".";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { AppKind } from "../../common/components/SerializedApp";
import type {
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import { getPrimitiveColumns, isPrimitiveType, makeEmptyComponentDescription } from "../../common/description";
import { getDocURL } from "../../common/docUrl";
import { assert } from "../../common/Support";
import { WireComponentKind } from "../../wire/component-kind";
import { getDefaultValuesForComponent, makeText, setColumnInRowForEditComponent } from "../builder-utils";
import { makeCaptionStringPropertyDescriptor, makeDefaultValuePropertyDescriptor } from "./descriptor-utils";
import type { FilePickerComponentDescription } from "./file-picker";
import { inflateFileOrImagePicker } from "./file-picker";
import { ComponentHandlerBase } from "./handler";
import { ImagePickerSourceHint } from "./image-picker-types";
import {
    ColumnPropertyFlag,
    ColumnPropertyHandler,
    EnumPropertyHandler,
    isRequiredPropertyHandler,
} from "./property-handlers";
import { PropertySection } from "./property-sections";
import { makeColumnProperty, makeEnumProperty } from "./property-utils";
import type { WireInflationBackend, WireRowComponentHydrator } from "./wire";

const ComponentKindImagePicker: ComponentKind = "image-picker";

const propertyNamePropertyHandler = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.EditedInApp,
        ColumnPropertyFlag.DefaultCaption,
    ],
    undefined,
    ["photo", "image"],
    getPrimitiveColumns,
    "image-uri",
    PropertySection.Data
);

const allSources: EnumPropertyCase<ImagePickerSourceHint>[] = [
    {
        value: ImagePickerSourceHint.Any,
        label: "Camera and Photo Roll",
    },
    {
        value: ImagePickerSourceHint.CameraOnly,
        label: "Camera Only",
    },
];

const cameraHintPropertyHandler = new EnumPropertyHandler(
    { cameraHint: ImagePickerSourceHint.Any },
    "Upload From",
    "Upload From",
    allSources,
    {
        name: "Upload From",
        order: 1000,
    },
    "radio"
);

interface ImagePickerComponentDescription extends FilePickerComponentDescription {
    readonly cameraHint: PropertyDescription | undefined;
}

export class ImagePickerComponentHandler extends ComponentHandlerBase<ImagePickerComponentDescription> {
    constructor() {
        super(ComponentKindImagePicker);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public get appKinds(): AppKind | "both" {
        return "both";
    }

    public needValidation(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ImagePickerComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = propertyNamePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, tables.output),
            undefined,
            tables,
            undefined,
            b.getContext,
            { debounce: false }
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);
        const cameraHint = cameraHintPropertyHandler.getEnum(desc);
        return b.let(
            lets,
            b.ui("ImagePicker", {
                value: valueToModify,
                onChange: action,
                isEnabled,
                validFlag: isRequired || mutatingScreenKind !== undefined ? validFlag : undefined,
                caption: makeText(builder, desc.caption, tables.input, true),
                isRequired: b.constant(isRequired),
                cameraHint: b.constant(cameraHint),
            })
        );
    }

    public getDescriptor(
        _desc: ImagePickerComponentDescription,
        _table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor {
        const properties: PropertyDescriptor[] = [
            propertyNamePropertyHandler,
            makeCaptionStringPropertyDescriptor("Image", false, mutatingScreenKind),
        ];
        if (mutatingScreenKind !== undefined) {
            properties.push(isRequiredPropertyHandler);

            if (ccc.appKind === AppKind.App) {
                const defaultDescr = makeDefaultValuePropertyDescriptor(ccc, mutatingScreenKind, "image-uri");
                if (defaultDescr !== undefined) {
                    properties.push(defaultDescr);
                }
            }
        }
        if (ccc.appKind === AppKind.App) {
            properties.push(cameraHintPropertyHandler);
        }
        properties.push(...this.getBasePropertyDescriptors());
        return {
            name: "Image Picker",
            description: "Upload an image",
            img: "componentImagePicker",
            group: ccc.appKind === AppKind.Page ? "Form Elements" : "Pickers",
            helpUrl: getDocURL("imagePicker"),
            properties,
        };
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: ImagePickerComponentDescription,
        { input }: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return getDefaultValuesForComponent(builder, desc, input, "image-uri");
    }

    public inflate(
        ib: WireInflationBackend,
        desc: ImagePickerComponentDescription
    ): WireRowComponentHydrator | undefined {
        const sourceHint = cameraHintPropertyHandler.getEnum(desc);

        return inflateFileOrImagePicker(ib, desc, p => ({
            ...p,
            kind: WireComponentKind.ImagePicker,
            sourceHint,
        }));
    }

    public static defaultComponent(column: TableColumn): ImagePickerComponentDescription {
        assert(isPrimitiveType(column.type));
        return {
            ...makeEmptyComponentDescription(ComponentKindImagePicker),
            propertyName: makeColumnProperty(column.name),
            defaultValue: undefined,
            caption: undefined,
            visibilityFilters: [],
            cameraHint: makeEnumProperty(ImagePickerSourceHint.Any),
        };
    }
}
